import React, { useState } from 'react';
import { Heart, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { createDonationCheckout } from '../../lib/paymentsApi';
import { toast } from 'sonner';

const PRESET_AMOUNTS = [25, 50, 75, 100];

export default function DonateButton() {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(25);
  const [custom, setCustom] = useState('');
  const [loading, setLoading] = useState(false);

  const amount = custom ? parseFloat(custom) : selected;

  const handleDonate = async () => {
    if (!amount || amount <= 0) {
      toast.error('Please choose or enter a donation amount.');
      return;
    }
    setLoading(true);
    try {
      const { checkout_url } = await createDonationCheckout(amount);
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(e.message || 'Could not start checkout. Please try again.');
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center justify-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-800 transition-colors min-h-[40px] min-w-[40px] sm:min-h-0 sm:min-w-0 px-3 py-1.5 rounded-full hover:bg-neutral-100"
        data-testid="donate-button"
      >
        <Heart className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Donate</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="donate-modal">
          <DialogHeader>
            <DialogTitle>Support Waxify</DialogTitle>
            <DialogDescription>
              Waxify is free to use. If it's been useful to you, a small donation helps keep it running.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-2">
            {PRESET_AMOUNTS.map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => { setSelected(a); setCustom(''); }}
                className={`h-10 rounded-lg border text-sm font-medium transition-colors ${
                  !custom && selected === a
                    ? 'bg-orange-600 border-orange-600 text-white'
                    : 'border-neutral-200 text-neutral-700 hover:bg-neutral-50'
                }`}
                data-testid={`donate-amount-${a}`}
              >
                ${a}
              </button>
            ))}
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-1.5">Or enter a custom amount</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 text-sm">$</span>
              <input
                type="number"
                min="1"
                step="1"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder="Custom amount"
                className="w-full h-10 pl-7 pr-3 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
                data-testid="donate-custom-amount-input"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={handleDonate}
            disabled={loading}
            className="flex items-center justify-center gap-2 w-full h-11 rounded-lg bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white text-sm font-semibold transition-colors"
            data-testid="donate-submit-button"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" />}
            {loading ? 'Redirecting…' : `Donate $${amount || 0}`}
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
}
